/**
 * Saves of the document and slides editors (plan 7.3): the whole
 * `ArtifactDocument`, at most about 30 KB, with `PUT document` 800 ms after
 * the last change, at once on Done, close and app background. The newest
 * document wins: a change during a save is sent when that save lands, on
 * the version it answered. Same states and failure handling as the page op
 * queue (retried twice, then Try again; a 409 stops the session for
 * Reload; the server's 422 and 429 words shown as they are), plus a local
 * check before sending: a document over a limit is held with the limit's
 * words until it fits again. `markNextSave('suggestion')` (the suggestions
 * card applying through the open editor) marks the next save
 * `via: 'suggestion'` for analytics; a retried save keeps the mark.
 */
import type { ArtifactDocument, ArtifactEditVia } from '@tabbio/types';
import type { ArtifactDocumentSaveResult } from '@/lib/artifact-edits-api';
import { classifySaveError, type SaveFailure } from './save-errors';
import { saveStateForFailure, type EditorSaveState } from './save-state';

export const DOCUMENT_SAVE_DEBOUNCE_MS = 800;
export const DOCUMENT_SAVE_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000];

export type DocumentSaveRequest = {
  baseVersionId: string;
  editSessionId: string;
  document: ArtifactDocument;
  /** Set when the save applies a suggestion (the `suggest-apply` analytics action). */
  via?: ArtifactEditVia;
};

export type DocumentSaveQueueDeps = {
  save: (request: DocumentSaveRequest) => Promise<ArtifactDocumentSaveResult>;
  /** A document was stored; `baseVersionId` is the version it was made against. */
  onSaved?: (result: ArtifactDocumentSaveResult, document: ArtifactDocument, baseVersionId: string) => void;
  onStateChange?: (state: EditorSaveState) => void;
  onFailure?: (failure: SaveFailure) => void;
  /** The words of a limit the document breaks, or null when it fits. */
  validate?: (document: ArtifactDocument) => string | null;
  setTimeout?: (callback: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  debounceMs?: number;
  retryDelaysMs?: readonly number[];
};

export class DocumentSaveQueue {
  private next: ArtifactDocument | null = null;
  private base: string;
  private inFlight: Promise<boolean> | null = null;
  private timer: unknown = null;
  private retries = 0;
  private stopped = false;
  private viaNext: ArtifactEditVia | null = null;
  private waiters: ((ok: boolean) => void)[] = [];
  private state: EditorSaveState = { status: 'idle' };
  private readonly setTimer: (callback: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly debounceMs: number;
  private readonly retryDelays: readonly number[];

  constructor(
    baseVersionId: string,
    readonly editSessionId: string,
    private readonly deps: DocumentSaveQueueDeps,
  ) {
    this.base = baseVersionId;
    this.setTimer = deps.setTimeout ?? ((callback, ms) => setTimeout(callback, ms));
    this.clearTimer = deps.clearTimeout ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
    this.debounceMs = deps.debounceMs ?? DOCUMENT_SAVE_DEBOUNCE_MS;
    this.retryDelays = deps.retryDelaysMs ?? DOCUMENT_SAVE_RETRY_DELAYS_MS;
  }

  get baseVersionId(): string {
    return this.base;
  }

  get saveState(): EditorSaveState {
    return this.state;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  hasUnsaved(): boolean {
    return this.next !== null || this.inFlight !== null;
  }

  /** The next save applies a suggestion (call it before `update`). */
  markNextSave(via: ArtifactEditVia) {
    if (!this.stopped) this.viaNext = via;
  }

  /** The editor's document after a change. */
  update(document: ArtifactDocument) {
    if (this.stopped) return;
    this.next = document;
    if (this.inFlight) return;
    const problem = this.deps.validate?.(document) ?? null;
    if (problem) {
      this.cancelTimer();
      this.setState({ status: 'invalid', message: problem });
      return;
    }
    this.setState({ status: 'dirty' });
    this.schedule();
  }

  flush(): Promise<boolean> {
    this.cancelTimer();
    if (this.stopped) return Promise.resolve(false);
    if (!this.inFlight && !this.next) return Promise.resolve(this.state.status !== 'failed');
    const done = new Promise<boolean>((resolve) => this.waiters.push(resolve));
    if (!this.inFlight) void this.run();
    return done;
  }

  retry(): Promise<boolean> {
    this.retries = 0;
    return this.flush();
  }

  reset(baseVersionId: string) {
    this.cancelTimer();
    this.next = null;
    this.base = baseVersionId;
    this.stopped = false;
    this.retries = 0;
    this.viaNext = null;
    this.settle(true);
    this.setState({ status: 'idle' });
  }

  dispose() {
    this.cancelTimer();
    this.stopped = true;
    this.settle(false);
  }

  private schedule() {
    this.cancelTimer();
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.run();
    }, this.debounceMs);
  }

  private cancelTimer() {
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
  }

  private setState(next: EditorSaveState) {
    this.state = next;
    this.deps.onStateChange?.(next);
  }

  private settle(ok: boolean) {
    const waiters = this.waiters;
    this.waiters = [];
    for (const resolve of waiters) resolve(ok);
  }

  private run(): Promise<boolean> {
    if (this.inFlight) return this.inFlight;
    const document = this.next;
    if (this.stopped || !document) {
      this.settle(!this.stopped);
      return Promise.resolve(!this.stopped);
    }
    const problem = this.deps.validate?.(document) ?? null;
    if (problem) {
      // Held until it fits; Done and close cannot store it either.
      this.setState({ status: 'invalid', message: problem });
      this.settle(false);
      return Promise.resolve(false);
    }
    this.next = null;
    const via = this.viaNext;
    this.viaNext = null;
    this.setState({ status: 'saving' });
    const saving = this.deps
      .save({ baseVersionId: this.base, editSessionId: this.editSessionId, document, ...(via ? { via } : {}) })
      .then(
        (result) => {
          this.inFlight = null;
          return this.onSuccess(result, document);
        },
        (error: unknown) => {
          this.inFlight = null;
          return this.onError(error, document, via);
        },
      );
    this.inFlight = saving;
    return saving;
  }

  private onSuccess(result: ArtifactDocumentSaveResult, document: ArtifactDocument): boolean {
    if (this.stopped) return false;
    this.retries = 0;
    const base = this.base;
    this.base = result.version.id;
    this.deps.onSaved?.(result, document, base);
    if (this.next) {
      if (this.waiters.length > 0) void this.run();
      else {
        this.setState({ status: 'dirty' });
        this.schedule();
      }
      return true;
    }
    this.setState({ status: 'saved' });
    this.settle(true);
    return true;
  }

  private onError(error: unknown, document: ArtifactDocument, via: ArtifactEditVia | null = null): boolean {
    if (this.stopped) return false;
    const failure = classifySaveError(error);
    // A newer document replaces the failed one (and holds the suggestion too); otherwise it is kept.
    this.next ??= document;
    if (via) this.viaNext = via;
    if (failure.kind === 'retryable' && this.retries < this.retryDelays.length) {
      const delay = this.retryDelays[this.retries] ?? 1_000;
      this.retries += 1;
      this.timer = this.setTimer(() => {
        this.timer = null;
        void this.run();
      }, delay);
      return false;
    }
    this.retries = 0;
    if (failure.kind === 'conflict' || failure.kind === 'gone' || failure.kind === 'disabled') {
      this.stopped = true;
      this.next = null;
      this.cancelTimer();
    } else if (failure.kind === 'invalid' && this.next === document) {
      // The same document cannot be stored; the next change tries again.
      this.next = null;
    }
    this.setState(saveStateForFailure(failure));
    this.deps.onFailure?.(failure);
    this.settle(false);
    return false;
  }
}
